import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../api";
import Header from "../components/Header";
import PlayerAvatar from "../components/PlayerAvatar";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Lock, Unlock, Play, Download, ChevronLeft, Trophy, Calculator, ArrowUp, ArrowDown } from "lucide-react";

// Adminpanel för säsonger: avsluta (arkivera + lås), öppna igen, starta nästa, ladda ner säkerhetskopia.
export default function AdminSeason() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);
  const [simBusy, setSimBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, lb] = await Promise.all([api.get("/season"), api.get("/leaderboard")]);
    setSeason(s.data);
    setRows([...lb.data.rows].sort((a, b) => b.rating - a.rating));
  }, []);
  useEffect(() => { load().catch((e) => toast.error(formatError(e.response?.data?.detail))); }, [load]);

  const act = async (path, confirmText, okText) => {
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try { await api.post(path); await load(); toast.success(okText); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runSim = async () => {
    setSimBusy(true);
    try { const { data } = await api.get("/admin/season/simulate", { params: { season: 1 } }); setSim(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSimBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `turn10-backup-${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Säkerhetskopian är nedladdad");
    } catch (e) { toast.error("Kunde inte ladda ner säkerhetskopian"); }
    finally { setBusy(false); }
  };

  if (!user?.is_admin) return (<div><Header title="Säsonger (admin)" /><p className="p-6 text-sm text-slate-400">Bara admin har tillgång hit.</p></div>);
  if (!season) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>;

  const n = season.season;
  const Btn = ({ onClick, tone, icon: Icon, children, testid }) => (
    <button data-testid={testid} disabled={busy} onClick={onClick}
      className={`w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60 ${tone}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {children}
    </button>
  );

  return (
    <div>
      <Header title="Säsonger (admin)" subtitle={`Säsong ${n} ${season.closed ? "– avslutad" : "– pågår"}`} />
      <div className="px-4 py-4 space-y-5">
        <button onClick={() => navigate("/min-sida")} className="flex items-center gap-1 text-slate-400 text-sm"><ChevronLeft className="w-4 h-4" /> Min sida</button>

        <div className={`p-4 rounded-2xl border ${season.closed ? "bg-rose-500/10 border-rose-500/40" : "bg-emerald-500/10 border-emerald-500/30"}`} data-testid="season-status">
          <div className="flex items-center gap-2 font-bold text-slate-100">{season.closed ? <Lock className="w-5 h-5 text-rose-300" /> : <Unlock className="w-5 h-5 text-emerald-300" />} Säsong {n} {season.closed ? "är avslutad och låst" : "pågår"}</div>
          <p className="text-xs text-slate-400 mt-1">Poängsystem: {season.elo_version === "v1" ? "säsong 1 (v1)" : "säsong 2 (v2: antal spelare, tillval, duell)"}.
            {season.closed ? " Inga nya matcher kan sparas förrän nästa säsong startar." : ""}</p>
        </div>

        {!season.closed && (
          <div>
            <Btn testid="close-season-button" tone="bg-amber-500 text-slate-950" icon={Trophy}
              onClick={() => act("/admin/season/close", `Avsluta säsong ${n}?\n\nSlutställningen sparas i arkivet, alla får sitt emblem, säsongen låses (inga nya matcher, inga ändringar) och en patch note som gratulerar vinnaren skapas.\n\nDu kan öppna säsongen igen om du behöver ändra något.`, `Säsong ${n} är avslutad`)}>Avsluta säsong {n}</Btn>
            <p className="text-[11px] text-slate-500 mt-1.5">Gör det när sista matchen är spelad. Allt sparas först i en säkerhetskopia.</p>
          </div>
        )}

        {season.closed && (
          <div className="space-y-3">
            <Btn testid="start-season-button" tone="bg-emerald-500 text-slate-950" icon={Play}
              onClick={() => act("/admin/season/start", `Starta säsong ${n + 1}?\n\nALLA spelare går tillbaka till 1000 Elo och all säsongsstatistik nollställs. Säsong ${n} ligger kvar låst i arkivet med alla emblem.\n\nEn patch note med alla ändringar publiceras.`, `Säsong ${n + 1} har startat!`)}>Starta säsong {n + 1}</Btn>
            <Btn testid="reopen-season-button" tone="bg-slate-800 border border-slate-600 text-slate-200" icon={Unlock}
              onClick={() => act("/admin/season/reopen", `Öppna säsong ${n} igen?\n\nArkivet och emblemen för säsongen tas bort och matcher kan sparas/ändras igen. Du avslutar den på nytt när du är klar.`, "Säsongen är öppnad igen")}>Öppna säsong {n} igen</Btn>
          </div>
        )}

        <div>
          <h2 className="text-sm font-bold font-display text-slate-200 mb-2">{season.closed ? "Slutställning" : "Ställning just nu"}</h2>
          {rows.length === 0 && <p className="text-sm text-slate-500">Inga matcher spelade ännu.</p>}
          <div className="space-y-1.5">
            {rows.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-900/60 border border-slate-800">
                <span className="w-6 text-center font-mono font-bold text-slate-400">{i + 1}</span>
                <PlayerAvatar icon={r.icon} name={r.name} size={30} />
                <span className="flex-1 truncate text-slate-100">{r.name}</span>
                <span className="font-mono font-bold text-amber-400">{r.rating}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-slate-800" data-testid="sim-card">
          <h2 className="text-sm font-bold font-display text-slate-200 mb-1 flex items-center gap-1.5"><Calculator className="w-4 h-4 text-violet-300" /> Vad hade det blivit? Säsong 1 med nya Elo</h2>
          <p className="text-[11px] text-slate-500 mb-2">Spelar upp alla säsong 1-matcher med säsong 2:s poängregler och visar hur topplistan hade sett ut. Allt räknas från matcherna som de ligger just nu, så har du rättat något syns det här. <b>Inget sparas.</b></p>
          <Btn testid="sim-button" tone="bg-violet-500 text-white" icon={Calculator} onClick={runSim}>{sim ? "Räkna om" : "Räkna ut"}</Btn>
          {simBusy && <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 text-violet-300 animate-spin" /></div>}
          {sim && (
            <div data-testid="sim-result" className="mt-3">
              <div className="text-[11px] text-slate-500 mb-1.5">{sim.matches} matcher · alla börjar på 1000 · ingen duell</div>
              <div className="space-y-1.5">
                {sim.rows.map((r) => (
                  <div key={r.user_id} data-testid={`sim-row-${r.user_id}`} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900/60 border border-slate-800">
                    <span className="w-6 text-center font-mono font-bold text-slate-300">{r.new_rank}</span>
                    <PlayerAvatar icon={r.icon} name={r.name} size={30} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-100 truncate">{r.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">idag #{r.actual_rank} · {r.actual_rating}</div>
                    </div>
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold ${r.rank_change > 0 ? "bg-emerald-500/15 text-emerald-300" : r.rank_change < 0 ? "bg-rose-500/15 text-rose-300" : "bg-slate-800 text-slate-400"}`}>
                      {r.rank_change > 0 ? <ArrowUp className="w-3 h-3" /> : r.rank_change < 0 ? <ArrowDown className="w-3 h-3" /> : null}{r.rank_change === 0 ? "=" : Math.abs(r.rank_change)}
                    </span>
                    <span className="font-mono font-bold text-amber-400 w-12 text-right">{r.new_rating}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-slate-800">
          <Btn testid="export-button" tone="bg-slate-800 border border-slate-600 text-slate-200" icon={Download} onClick={download}>Ladda ner säkerhetskopia (fil)</Btn>
          <p className="text-[11px] text-slate-500 mt-1.5">Alla matcher, spelare, regler, kommentarer och arkiv som en JSON-fil (utan lösenord). Gör det regelbundet.</p>
        </div>
      </div>
    </div>
  );
}
